import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCompanyEventTemplatesCacheKey,
  FetchFinCompanyEventTemplatesFilters,
  FetchCompanyEventTemplatesGqlQuery,
  FetchCompanyEventTemplatesLimit,
  FetchCompanyEventTemplatesPage,
  FetchCompanyEventTemplatesSort
} from '../../companyEventTemplatesTypes';

import { FetchCacheCompanyEventTemplateQueryResponse } from '../../queries/fetchCacheCompanyEventTemplates.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_COMPANY_EVENT_TEMPLATES_FILTERS,
  INITIAL_COMPANY_EVENT_TEMPLATES_LIMIT,
  INITIAL_COMPANY_EVENT_TEMPLATES_PAGE,
  INITIAL_COMPANY_EVENT_TEMPLATES_SORT
} from '../../companyEventTemplatesConstants';

interface CompanyEventTemplatesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface CompanyEventTemplatesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface CompanyEventTemplatesDefaultOptions {
  action?: string;
  cacheKey: FetchCompanyEventTemplatesCacheKey;
  initialFilters?: FetchFinCompanyEventTemplatesFilters;
  initialLimit?: FetchCompanyEventTemplatesLimit;
  initialPage?: FetchCompanyEventTemplatesPage;
  initialSort?: FetchCompanyEventTemplatesSort;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    keepPreviousData?: boolean;
    staleTime?: number;
    withoutPrefetch?: boolean;
  };
  query: FetchCompanyEventTemplatesGqlQuery;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
}

type CompanyEventTemplatesOptions = CompanyEventTemplatesDefaultOptions &
  (
    | CompanyEventTemplatesWithPrefetchItemOptions
    | CompanyEventTemplatesWithoutPrefetchItemOptions
  );

const scope = 'companyEventTemplates';

function useCachePaginatedCompanyEventTemplates<
  FetchCompanyEventTemplatesType = FetchCacheCompanyEventTemplateQueryResponse
>({
  action,
  cacheKey,
  fetchItemCacheKey,
  fetchItemQuery,
  initialFilters = INITIAL_COMPANY_EVENT_TEMPLATES_FILTERS,
  initialLimit = INITIAL_COMPANY_EVENT_TEMPLATES_LIMIT,
  initialPage = INITIAL_COMPANY_EVENT_TEMPLATES_PAGE,
  initialSort = INITIAL_COMPANY_EVENT_TEMPLATES_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits
}: CompanyEventTemplatesOptions) {
  const {
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    currentFilters,
    currentLimit,
    currentPage,
    currentSort,
    data,
    filterItems,
    isFetched,
    isLoading,
    isPlaceholderData,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    limitItems,
    paginateItems,
    prefetchItem,
    prefetchItems,
    sortItems,
    updateItemCache,
    updateItemsCache
  } = useCacheIndexQuery<FetchCompanyEventTemplatesType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    changeCompanyEventTemplatesFilters: changeItemsFilters,
    clearCompanyEventTemplatesFilters: clearItemsFilters,
    clearCompanyEventTemplatesFiltersPersistInitial:
      clearItemsFiltersPersistInitial,
    companyEventTemplates: items,
    companyEventTemplatesData: data,
    companyEventTemplatesError: itemsError,
    companyEventTemplatesErrorMessage: itemsErrorMessage,
    companyEventTemplatesFetched: isFetched,
    companyEventTemplatesFilters: currentFilters,
    companyEventTemplatesIsPlaceholderData: isPlaceholderData,
    companyEventTemplatesLimit: currentLimit,
    companyEventTemplatesLoading: isLoading,
    companyEventTemplatesPage: currentPage,
    companyEventTemplatesSort: currentSort,
    companyEventTemplatesTotalCount: itemsTotalCount,
    filterCompanyEventTemplates: filterItems,
    limitCompanyEventTemplates: limitItems,
    paginateCompanyEventTemplates: paginateItems,
    prefetchCompanyEventTemplate: prefetchItem,
    prefetchCompanyEventTemplates: prefetchItems,
    sortCompanyEventTemplates: sortItems,
    updateCompanyEventTemplateCache: updateItemCache,
    updateCompanyEventTemplatesCache: updateItemsCache
  };
}

export default useCachePaginatedCompanyEventTemplates;
